// FUNCTIONALITY-115B-R2 — shared brand normalization / search service.

export interface BrandCandidate {
  name: string;
  slug: string;
}

export interface BrandSearchHit {
  brand: BrandCandidate;
  score: number;
  reason:
    | "exact_canonical"
    | "exact_normalized"
    | "keyboard"
    | "alias"
    | "transliteration"
    | "phonetic"
    | "prefix"
    | "token"
    | "fuzzy";
}

const EN_TO_UA =
  "qwertyuiop[]asdfghjkl;'zxcvbnm,./QWERTYUIOP{}ASDFGHJKL:\"ZXCVBNM<>?йцукенгшщзхїфівапролджєячсмитьбю.ЙЦУКЕНГШЩЗХЇФІВАПРОЛДЖЄЯЧСМИТЬБЮ,";
const EN_TO_RU =
  "qwertyuiop[]asdfghjkl;'zxcvbnm,./QWERTYUIOP{}ASDFGHJKL:\"ZXCVBNM<>?йцукенгшщзхъфывапролджэячсмитьбю.ЙЦУКЕНГШЩЗХЪФЫВАПРОЛДЖЭЯЧСМИТЬБЮ,";

const VISUAL_MAP: Record<string, string> = {
  a: "а",
  A: "А",
  B: "В",
  e: "е",
  E: "Е",
  i: "і",
  I: "І",
  K: "К",
  M: "М",
  H: "Н",
  O: "О",
  o: "о",
  P: "Р",
  C: "С",
  T: "Т",
  X: "Х",
  y: "у",
};

export const CANONICAL_ALIASES: Record<string, string[]> = {
  Kikkoman: ["kikkoman", "kikoman", "kikkomen", "кіккоман", "киккоман", "кікоман", "кикоман"],
  Kinder: [
    "kinder",
    "кін",
    "кин",
    "кіндер",
    "киндер",
    "кіндеп",
    "киндеп",
    "кіндер рігель",
    "киндер ригель",
    "kinder riegel",
  ],
  "Aroy-D": ["aroy-d", "aroy d", "aroyd", "арой д", "арой-д", "аройд", "аройда", "aroida"],
  "Royal Family": [
    "royal family",
    "royalfamily",
    "rojal family",
    "роял фемілі",
    "роял фемили",
    "ройал фемілі",
  ],
  "Lucky Me": ["lucky me!", "lucky me", "luckyme", "laki mi", "лакі мі", "лаки ми"],
  Nongshim: ["nongshim", "nong shim", "нонгшим", "нонг шім"],
  Samyang: ["samyang", "самянг", "самяң"],
};

const PHONETIC_ALIASES: Record<string, string[]> = {
  Kikkoman: ["kikoman", "кикоман"],
  "Aroy-D": ["aroid", "аройд", "аройда"],
  "Royal Family": ["royal famili", "роял фемили"],
  "Lucky Me": ["laki mi", "лаки ми"],
};

function mapChars(input: string, from: string, to: string): string {
  let out = "";
  for (const ch of input) {
    const idx = from.indexOf(ch);
    out += idx >= 0 ? to[idx]! : ch;
  }
  return out;
}

export function isProtectedNumericQuery(query: string): boolean {
  const compact = String(query ?? "").replace(/[\s\-_/]+/g, "");
  return /^[+#*]?[\d]{4,}$/.test(compact) || /^STG-\d+$/i.test(String(query ?? "").trim());
}

export function normalizeBrandQuery(value: string | null | undefined): string {
  return String(value ?? "")
    .normalize("NFKC")
    .toLocaleLowerCase("en")
    .trim()
    .replace(/[’'`´]/g, "'")
    .replace(/[‐‑‒–—―]/g, "-")
    .replace(/[!?.,:;]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function collapseBrand(value: string): string {
  return normalizeBrandQuery(value).replace(/[\s\-_'"]+/g, "");
}

function translitToLatin(value: string): string {
  const map: Record<string, string> = {
    а: "a", б: "b", в: "v", г: "h", ґ: "g", д: "d", е: "e", є: "ye", ж: "zh", з: "z",
    и: "y", і: "i", ї: "yi", й: "y", к: "k", л: "l", м: "m", н: "n", о: "o", п: "p",
    р: "r", с: "s", т: "t", у: "u", ф: "f", х: "kh", ц: "ts", ч: "ch", ш: "sh", щ: "shch",
    ь: "", ю: "yu", я: "ya", ы: "y", э: "e", ё: "yo", ъ: "",
  };
  return [...value].map((ch) => map[ch] ?? ch).join("");
}

function visualNormalize(value: string): string {
  return [...value].map((ch) => VISUAL_MAP[ch] ?? ch).join("");
}

/** Resolve a curated brand alias (Kikoman → Kikkoman). */
export function resolveCuratedBrandAlias(query: string): string | null {
  const base = normalizeBrandQuery(query);
  const compact = collapseBrand(query);
  if (!base) return null;
  const probe = new Set<string>([base, compact]);
  // Lightweight layout/translit probes (avoid calling generateBrandVariants to keep this pure).
  probe.add(normalizeBrandQuery(translitToLatin(base)));
  probe.add(collapseBrand(translitToLatin(base)));
  probe.add(
    normalizeBrandQuery(
      mapChars(base, EN_TO_UA.slice(0, EN_TO_UA.length / 2), EN_TO_UA.slice(EN_TO_UA.length / 2)),
    ),
  );
  for (const [canonical, aliases] of Object.entries(CANONICAL_ALIASES)) {
    const all = [canonical, ...aliases, ...(PHONETIC_ALIASES[canonical] ?? [])];
    for (const a of all) {
      const na = normalizeBrandQuery(a);
      const ca = collapseBrand(a);
      if (probe.has(na) || probe.has(ca)) return canonical;
    }
  }
  return null;
}

export function generateBrandVariants(query: string): string[] {
  const base = normalizeBrandQuery(query);
  const variants = new Set<string>([base, collapseBrand(base)]);
  if (!base) return [];
  const layouts = [
    mapChars(base, EN_TO_UA.slice(0, EN_TO_UA.length / 2), EN_TO_UA.slice(EN_TO_UA.length / 2)),
    mapChars(base, EN_TO_UA.slice(EN_TO_UA.length / 2), EN_TO_UA.slice(0, EN_TO_UA.length / 2)),
    mapChars(base, EN_TO_RU.slice(0, EN_TO_RU.length / 2), EN_TO_RU.slice(EN_TO_RU.length / 2)),
    mapChars(base, EN_TO_RU.slice(EN_TO_RU.length / 2), EN_TO_RU.slice(0, EN_TO_RU.length / 2)),
  ];
  for (const layout of layouts) {
    variants.add(normalizeBrandQuery(layout));
    variants.add(collapseBrand(layout));
  }
  variants.add(normalizeBrandQuery(visualNormalize(base)));
  variants.add(normalizeBrandQuery(translitToLatin(base)));
  variants.add(collapseBrand(translitToLatin(base)));
  // Include curated aliases when the query matches any alias (Kikoman → kikkoman).
  for (const [canonical, aliases] of Object.entries(CANONICAL_ALIASES)) {
    const pool = [canonical, ...aliases, ...(PHONETIC_ALIASES[canonical] ?? [])];
    const hit = pool.some((a) => {
      const na = normalizeBrandQuery(a);
      const ca = collapseBrand(a);
      return variants.has(na) || variants.has(ca) || na === base || ca === collapseBrand(base);
    });
    if (!hit) continue;
    for (const a of pool) {
      variants.add(normalizeBrandQuery(a));
      variants.add(collapseBrand(a));
    }
  }
  return [...variants].filter(Boolean);
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const row = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    let prev = i - 1;
    row[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const cur = row[j]!;
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      row[j] = Math.min(row[j]! + 1, row[j - 1]! + 1, prev + cost);
      prev = cur;
    }
  }
  return row[b.length]!;
}

function aliasHit(canonical: string, queryVariants: string[]): boolean {
  const aliases = [
    ...(CANONICAL_ALIASES[canonical] ?? []),
    ...(PHONETIC_ALIASES[canonical] ?? []),
    normalizeBrandQuery(canonical),
    collapseBrand(canonical),
  ];
  return queryVariants.some((q) => aliases.includes(q) || aliases.includes(collapseBrand(q)));
}

export function scoreBrandMatch(query: string, brandName: string): BrandSearchHit["reason"] | null {
  if (isProtectedNumericQuery(query)) return null;
  const variants = generateBrandVariants(query);
  const brandNorm = normalizeBrandQuery(brandName);
  const brandCompact = collapseBrand(brandName);
  if (variants.includes(brandNorm)) return "exact_normalized";
  if (variants.includes(brandCompact)) return "exact_normalized";
  if (aliasHit(brandName, variants)) {
    if ((PHONETIC_ALIASES[brandName] ?? []).some((a) => variants.includes(normalizeBrandQuery(a)))) {
      return "phonetic";
    }
    return "alias";
  }
  for (const v of variants) {
    if (v.length >= 3 && (brandNorm.startsWith(v) || brandCompact.startsWith(collapseBrand(v)))) return "prefix";
    if (brandNorm.split(" ").some((token) => token === v)) return "token";
    if (translitToLatin(brandNorm) === v || translitToLatin(brandCompact) === collapseBrand(v)) {
      return "transliteration";
    }
  }
  for (const v of variants) {
    if (v.length < 4 || brandCompact.length < 4) continue;
    const dist = levenshtein(collapseBrand(v), brandCompact);
    if (dist > 0 && dist <= Math.max(1, Math.floor(brandCompact.length / 5))) return "fuzzy";
  }
  for (const v of variants) {
    if (v !== normalizeBrandQuery(query) && (v === brandNorm || collapseBrand(v) === brandCompact)) {
      return "keyboard";
    }
  }
  return null;
}

const REASON_SCORE: Record<BrandSearchHit["reason"], number> = {
  exact_canonical: 100,
  exact_normalized: 95,
  keyboard: 90,
  alias: 85,
  transliteration: 80,
  phonetic: 75,
  prefix: 70,
  token: 65,
  fuzzy: 55,
};

export function matchBrands(query: string, brands: BrandCandidate[]): {
  hits: BrandSearchHit[];
  didYouMean: BrandCandidate | null;
} {
  const q = String(query ?? "").trim();
  if (!q || isProtectedNumericQuery(q)) return { hits: [], didYouMean: null };
  const hits: BrandSearchHit[] = [];
  for (const brand of brands) {
    const exact = normalizeBrandQuery(q) === normalizeBrandQuery(brand.name);
    const reason = exact ? ("exact_canonical" as const) : scoreBrandMatch(q, brand.name);
    if (!reason) continue;
    hits.push({ brand, score: REASON_SCORE[reason], reason });
  }
  hits.sort((a, b) => b.score - a.score || a.brand.name.localeCompare(b.brand.name));
  const top = hits[0] ?? null;
  const didYouMean =
    top && top.score < REASON_SCORE.exact_normalized && top.score >= REASON_SCORE.fuzzy ? top.brand : null;
  return { hits, didYouMean };
}

export function normalizeBrandName(value: string | null | undefined): string | null {
  const compact = String(value ?? "").normalize("NFKC").replace(/\s+/g, " ").trim();
  if (!compact) return null;
  const norm = normalizeBrandQuery(compact);
  const collapsed = collapseBrand(compact);
  for (const [canonical, aliases] of Object.entries(CANONICAL_ALIASES)) {
    const all = [canonical, ...aliases].map((item) => normalizeBrandQuery(item));
    if (all.includes(norm) || all.includes(collapsed)) return canonical;
    const canonNorm = normalizeBrandQuery(canonical);
    const canonCompact = collapseBrand(canonical);
    // "Kikkoman Corporation" / "Lucky Me! Foods" → canonical brand.
    if (norm === canonNorm || norm.startsWith(`${canonNorm} `) || collapsed.startsWith(canonCompact)) {
      return canonical;
    }
  }
  return compact;
}
